import { useEffect, useRef, useMemo, useCallback, useState } from "react";
import Quill from "quill";
import { ImageData } from "quill-image-drop-and-paste";
import { QuillModules, QuillFormats } from "./types";
import useDialogAlert from "@hooks/useDialogAlert";

interface UseQuillEditorProps {
  value?: string;
  onChange?: (html: string, text: string, quill: Quill) => void;
  onBlur?: (html: string, text: string, quill: Quill) => void;
  onImageUpload?: (files: File[]) => Promise<string[]>;
  placeholder?: string;
  readOnly?: boolean;
  theme?: "snow" | "bubble";
  modules?: QuillModules;
  formats?: QuillFormats;
  maxLength?: number;
  isDisabled?: boolean;
}

export const useQuillEditor = ({
  value = "",
  onChange,
  onBlur,
  onImageUpload,
  placeholder = "",
  readOnly = false,
  theme = "snow",
  modules: customModules,
  formats: customFormats,
  maxLength,
  isDisabled = false,
}: UseQuillEditorProps) => {
  const { snackbar } = useDialogAlert();
  const editorRef = useRef<HTMLDivElement>(null);
  const quillRef = useRef<Quill | null>(null);
  const isInitialMount = useRef(true);
  const [modalImage, setModalImage] = useState<string | null>(null);

  const uploadImages = useCallback(
    async (files: File[]) => {
      if (!quillRef.current || !onImageUpload) return;

      const editor = quillRef.current;
      const range = editor.getSelection(true);

      try {
        const imageUrls = await onImageUpload(files);
        let currentCursorIndex = range.index;

        imageUrls.forEach((url) => {
          editor.insertEmbed(currentCursorIndex, "image", url);
          currentCursorIndex++;
        });

        editor.setSelection(currentCursorIndex, 0);
      } catch (error) {
        snackbar({
          message: "Erro ao enviar imagem, tente novamente.",
          variant: "error",
        });
      }
    },
    [onImageUpload, snackbar]
  );

  const imageHandler = useCallback(() => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.setAttribute("multiple", "true");
    input.click();

    input.onchange = async () => {
      const files = input.files;
      if (!files || !files.length) return;

      await uploadImages(Array.from(files));
      input.value = "";
    };
  }, [uploadImages]);

  const defaultModules: QuillModules = useMemo(
    () => ({
      imageDropAndPaste: onImageUpload
        ? {
            handler: async (
              dataUrl: string,
              type: string,
              imageData: ImageData
            ) => {
              const file = imageData.toFile();
              if (file) {
                await uploadImages([file]);
              }
            },
          }
        : false,
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ["bold", "italic", "underline", "strike"],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ color: [] }, { background: [] }],
          ["image"],
        ],
        handlers: {
          image: onImageUpload ? imageHandler : undefined,
        },
      },
      clipboard: {
        matchVisual: false,
      },
    }),
    [onImageUpload, imageHandler, uploadImages]
  );

  const defaultFormats: QuillFormats = useMemo(
    () => [
      "header",
      "bold",
      "italic",
      "underline",
      "strike",
      "list",
      "bullet",
      "image",
      "color",
      "background",
    ],
    []
  );

  const modules = customModules || defaultModules;
  const formats = customFormats || defaultFormats;

  useEffect(() => {
    if (!editorRef.current || quillRef.current) return;

    const quill = new Quill(editorRef.current, {
      theme,
      modules: readOnly ? { toolbar: false } : modules,
      formats,
      placeholder,
      readOnly: readOnly || isDisabled,
    });

    quillRef.current = quill;

    if (value) {
      quill.clipboard.dangerouslyPasteHTML(value);
    }

    quill.on("text-change", () => {
      const html = quill.root.innerHTML;
      const text = quill.getText();

      if (maxLength && text.length > maxLength) {
        quill.deleteText(maxLength, text.length);
        return;
      }

      onChange?.(html, text, quill);
    });

    quill.on("selection-change", () => {
      onBlur?.(quill.root.innerHTML, quill.getText(), quill);
    });

    const handleImageClick = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.tagName === "IMG") {
        setModalImage((target as HTMLImageElement).src);
      }
    };

    quill.root.addEventListener("click", handleImageClick);

    return () => {
      quill.off("text-change");
      quill.off("selection-change");
      quill.root.removeEventListener("click", handleImageClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!quillRef.current || isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const currentContent = quillRef.current.root.innerHTML;
    if (currentContent !== value) {
      const selection = quillRef.current.getSelection();
      quillRef.current.clipboard.dangerouslyPasteHTML(value);
      if (selection) {
        quillRef.current.setSelection(selection);
      }
    }
  }, [value]);

  useEffect(() => {
    if (quillRef.current) {
      quillRef.current.enable(!readOnly && !isDisabled);
    }
  }, [readOnly, isDisabled]);

  const charCount = useMemo(() => {
    if (!quillRef.current) return 0;
    return quillRef.current.getText().trim().length;
  }, []);

  return {
    editorRef,
    modalImage,
    setModalImage,
    charCount,
  };
};
